/**
 * Tier Detector - Determines whether an ontology is a taxonomy, ontology, or knowledge graph
 *
 * Pure function: takes statistics + optional structural analysis, returns tier detection result.
 */

import type {
  KnowledgeTier,
  TierDetectionResult,
  TierSignal,
  OntologyStatistics,
  StructuralAnalysisData,
} from './types';

/**
 * Detect the knowledge architecture tier of an ontology.
 *
 * Algorithm:
 * - TAXONOMY: has classes + hierarchy but no/few typed properties, no individuals
 * - ONTOLOGY: has classes + typed properties (domain/range), few/no individuals
 * - KNOWLEDGE_GRAPH: has classes + properties + significant individual population
 */
export function detectTier(
  stats: OntologyStatistics,
  structural?: StructuralAnalysisData | null
): TierDetectionResult {
  const signals: TierSignal[] = [];
  const hasClasses = stats.class_count > 0;
  const hasProperties = stats.property_count > 0;
  const hasIndividuals = stats.individual_count > 0;
  const individualRatio = stats.class_count > 0
    ? stats.individual_count / stats.class_count
    : 0;

  // Domain/range coverage from structural analysis
  const domainPct = structural?.propertiesWithDomain?.percentage ?? 0;
  const rangePct = structural?.propertiesWithRange?.percentage ?? 0;
  const hasTypedProperties = domainPct >= 20 || rangePct >= 20;

  // Collect signals
  signals.push({
    label: 'Has classes',
    met: hasClasses,
    value: `${stats.class_count} classes`,
  });

  signals.push({
    label: 'Has hierarchy depth',
    met: stats.hierarchy_depth >= 1,
    value: `Depth ${stats.hierarchy_depth}`,
  });

  signals.push({
    label: 'Has properties',
    met: hasProperties,
    value: `${stats.property_count} properties`,
  });

  signals.push({
    label: 'Has typed properties (domain/range >= 20%)',
    met: hasTypedProperties,
    value: `Domain ${domainPct.toFixed(0)}%, Range ${rangePct.toFixed(0)}%`,
  });

  signals.push({
    label: 'Has individuals',
    met: hasIndividuals,
    value: `${stats.individual_count} individuals`,
  });

  signals.push({
    label: 'Individual-to-class ratio >= 0.1',
    met: individualRatio >= 0.1,
    value: `Ratio ${individualRatio.toFixed(2)}`,
  });

  // Determine tier
  let tier: KnowledgeTier;
  let confidence: 'high' | 'medium' | 'low';
  let nextTierActions: string[];

  if (hasClasses && hasProperties && hasIndividuals && individualRatio >= 0.1) {
    // Knowledge Graph
    tier = 'knowledge_graph';
    confidence = individualRatio >= 0.5 ? 'high' : individualRatio >= 0.2 ? 'medium' : 'low';
    nextTierActions = [
      'Ensure all individuals have rdf:type declarations',
      'Add cross-references between instance data',
      'Validate instance data against SHACL shapes',
      'Consider adding provenance (PROV-O) to instances',
    ];
  } else if (hasClasses && hasProperties && hasTypedProperties) {
    // Ontology
    tier = 'ontology';
    confidence = (domainPct >= 50 && rangePct >= 50) ? 'high' : 'medium';
    nextTierActions = [
      'Add instance data (individuals) to evolve into a knowledge graph',
      `Improve domain coverage (currently ${domainPct.toFixed(0)}%)`,
      `Improve range coverage (currently ${rangePct.toFixed(0)}%)`,
      'Add SHACL shape constraints for validation',
      'Define disjointness axioms between sibling classes',
    ];
  } else {
    // Taxonomy (default / fallback)
    tier = 'taxonomy';
    confidence = hasClasses && stats.hierarchy_depth >= 2 ? 'high'
      : hasClasses ? 'medium'
      : 'low';
    nextTierActions = [
      'Add object/datatype properties with domain and range declarations',
      'Define relationships beyond parent-child hierarchy',
      'Add rdfs:label and rdfs:comment to all classes',
      'Consider adopting an upper ontology (BFO, PROV-O)',
    ];
  }

  return { tier, confidence, signals, nextTierActions };
}
